import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SampleTag } from "../components/SampleTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROVIDERS = [
    { id: "chatgpt", label: "ChatGPT" },
    { id: "claude", label: "Claude" },
    { id: "gemini", label: "Gemini" },
];
const VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"];

const TabButton = ({ id, active, onClick, children }) => (
    <button
        type="button"
        data-testid={`studio-tab-${id}`}
        onClick={onClick}
        className={`border px-5 py-2.5 text-[13px] tracking-wide transition-colors duration-300 ${
            active
                ? "border-pine bg-pine text-paper"
                : "border-line bg-transparent text-ink-muted hover:border-brass hover:text-brass"
        }`}
    >
        {children}
    </button>
);

const Studio = () => {
    const { user, userLoading } = useAuth();
    const [tab, setTab] = useState("chat");
    const [busy, setBusy] = useState(false);

    const [chatProvider, setChatProvider] = useState("chatgpt");
    const [prompt, setPrompt] = useState("");
    const [chatResult, setChatResult] = useState(null);
    const [chatError, setChatError] = useState("");

    const [imagePrompt, setImagePrompt] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [imageError, setImageError] = useState("");
    const [gallery, setGallery] = useState([]);

    const [ttsText, setTtsText] = useState("Welcome to The Panel Room. Take your seat.");
    const [voice, setVoice] = useState("alloy");
    const [audioUrl, setAudioUrl] = useState("");
    const [transcript, setTranscript] = useState("");
    const [voiceError, setVoiceError] = useState("");

    const [bookings, setBookings] = useState([]);
    const [messages, setMessages] = useState([]);

    useEffect(() => {
        if (user?.role === "admin") {
            fetch(`${API}/admin/bookings`, { credentials: "include" })
                .then((r) => (r.ok ? r.json() : { bookings: [] }))
                .then((d) => setBookings(d.bookings || []))
                .catch(() => {});
            fetch(`${API}/admin/messages`, { credentials: "include" })
                .then((r) => (r.ok ? r.json() : { messages: [] }))
                .then((d) => setMessages(d.messages || []))
                .catch(() => {});
        }
    }, [user]);

    useEffect(() => {
        if (user?.role === "admin" && tab === "image" && gallery.length === 0) {
            fetch(`${API}/ai/images`, { credentials: "include" })
                .then((r) => (r.ok ? r.json() : { images: [] }))
                .then((d) => setGallery(d.images || []))
                .catch(() => {});
        }
    }, [tab, user, gallery.length]);

    if (userLoading) {
        return (
            <main className="py-32 text-center" data-testid="studio-loading">
                <p className="text-sm text-ink-muted">Checking your seat…</p>
            </main>
        );
    }

    if (!user || user.role !== "admin") {
        return (
            <main className="py-32 text-center" data-testid="studio-gate">
                <p className="eyebrow">Studio</p>
                <h2 className="mt-4 font-display text-3xl tracking-tight sm:text-4xl">
                    Admin access <em className="italic text-brass">only.</em>
                </h2>
                <p className="mx-auto mt-4 max-w-md text-sm text-ink-muted">
                    The AI Studio is reserved for the site owner. Sign in with an
                    admin account to continue.
                </p>
                <Link to="/login" className="btn-primary mt-8" data-testid="studio-signin-link">
                    Sign in
                </Link>
            </main>
        );
    }

    const run = async (fn) => {
        setBusy(true);
        try {
            await fn();
        } finally {
            setBusy(false);
        }
    };

    const ask = () =>
        run(async () => {
            setChatError("");
            setChatResult(null);
            try {
                const res = await fetch(`${API}/ai/chat`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ prompt, provider: chatProvider }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
                setChatResult(data);
            } catch (e) {
                setChatError(e.message);
            }
        });

    const generateImage = () =>
        run(async () => {
            setImageError("");
            setImageUrl("");
            try {
                const res = await fetch(`${API}/ai/image`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ prompt: imagePrompt }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
                setImageUrl(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
                setGallery((g) => [{ id: data.id, prompt: data.prompt, url: data.url, storage_path: "new" }, ...g]);
            } catch (e) {
                setImageError(e.message);
            }
        });

    const speak = () =>
        run(async () => {
            setVoiceError("");
            try {
                const res = await fetch(`${API}/ai/tts`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ text: ttsText, voice }),
                });
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
                }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                setTranscript("");
            } catch (e) {
                setVoiceError(e.message);
            }
        });

    const transcribe = (file) =>
        run(async () => {
            setVoiceError("");
            try {
                const body = new FormData();
                body.append("audio", file);
                const res = await fetch(`${API}/ai/stt`, {
                    method: "POST",
                    credentials: "include",
                    body,
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
                setTranscript(data.text);
                setAudioUrl("");
            } catch (e) {
                setVoiceError(e.message);
            }
        });

    return (
        <main className="py-20 md:py-28" data-testid="studio-page">
            <div className="wrap">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="eyebrow">AI Studio · Admin</p>
                        <h2 className="mt-4 text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            The owner&apos;s <em className="italic text-brass">back room.</em>
                        </h2>
                    </div>
                    <SampleTag />
                </div>

                <div className="mt-10 flex flex-wrap gap-2">
                    <TabButton id="chat" active={tab === "chat"} onClick={() => setTab("chat")}>
                        AI chat
                    </TabButton>
                    <TabButton id="image" active={tab === "image"} onClick={() => setTab("image")}>
                        Image generation
                    </TabButton>
                    <TabButton id="voice" active={tab === "voice"} onClick={() => setTab("voice")}>
                        Voice (TTS · STT)
                    </TabButton>
                    <TabButton id="bookings" active={tab === "bookings"} onClick={() => setTab("bookings")}>
                        Bookings
                    </TabButton>
                    <TabButton id="messages" active={tab === "messages"} onClick={() => setTab("messages")}>
                        Messages
                    </TabButton>
                </div>

                {tab === "chat" && (
                    <div className="mt-8 border border-line bg-surface p-8 sm:p-10" data-testid="studio-chat">
                        <label htmlFor="ai-provider" className="field-label">
                            Model provider
                        </label>
                        <div className="mb-6 flex flex-wrap gap-2">
                            {PROVIDERS.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    data-testid={`provider-${p.id}`}
                                    onClick={() => setChatProvider(p.id)}
                                    className={`border px-4 py-2 text-sm transition-colors duration-300 ${
                                        chatProvider === p.id
                                            ? "border-brass text-brass"
                                            : "border-line text-ink-muted hover:border-brass hover:text-brass"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <label htmlFor="ai-prompt" className="field-label">
                            Prompt
                        </label>
                        <textarea
                            id="ai-prompt"
                            data-testid="studio-chat-prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={3}
                            maxLength={4000}
                            placeholder="Ask anything…"
                            className="field resize-none border border-line px-3"
                        />
                        <button
                            type="button"
                            onClick={ask}
                            disabled={busy || !prompt.trim()}
                            data-testid="studio-chat-btn"
                            className="btn-primary mt-6 disabled:opacity-60"
                        >
                            {busy ? "Thinking…" : "Ask"}
                        </button>
                        {chatError && (
                            <p className="mt-4 text-sm text-red-700 dark:text-red-400" data-testid="studio-chat-error">
                                {chatError}
                            </p>
                        )}
                        {chatResult && (
                            <div className="mt-8 border-t border-line pt-6" data-testid="studio-chat-result">
                                <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-brass">
                                    {chatResult.provider} · {chatResult.model}
                                </p>
                                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink">
                                    {chatResult.text}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {tab === "image" && (
                    <div className="mt-8 border border-line bg-surface p-8 sm:p-10" data-testid="studio-image">
                        <label htmlFor="ai-image-prompt" className="field-label">
                            Image prompt
                        </label>
                        <textarea
                            id="ai-image-prompt"
                            data-testid="studio-image-prompt"
                            value={imagePrompt}
                            onChange={(e) => setImagePrompt(e.target.value)}
                            rows={2}
                            maxLength={1000}
                            placeholder="A dignified wood-panelled interview room, editorial illustration…"
                            className="field resize-none border border-line px-3"
                        />
                        <button
                            type="button"
                            onClick={generateImage}
                            disabled={busy || imagePrompt.trim().length < 3}
                            data-testid="studio-image-btn"
                            className="btn-primary mt-6 disabled:opacity-60"
                        >
                            {busy ? "Generating…" : "Generate image"}
                        </button>
                        {imageError && (
                            <p className="mt-4 text-sm text-red-700 dark:text-red-400" data-testid="studio-image-error">
                                {imageError}
                            </p>
                        )}
                        {imageUrl && (
                            <figure className="mt-8" data-testid="studio-image-result">
                                <img
                                    src={imageUrl}
                                    alt="Generated"
                                    className="max-h-[480px] w-full border border-line object-contain"
                                />
                                <figcaption className="mt-3 flex items-center justify-between text-xs text-ink-muted">
                                    <span>Stored via the file &amp; media storage integration.</span>
                                    <a
                                        href={imageUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="studio-image-download"
                                        className="u-link inline-flex items-center gap-2 text-brass"
                                    >
                                        <Download size={13} strokeWidth={1.5} />
                                        Open / download
                                    </a>
                                </figcaption>
                            </figure>
                        )}

                        {gallery.length > 0 && (
                            <div className="mt-10 border-t border-line pt-8">
                                <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink-muted">
                                    Generated gallery · {gallery.length}
                                </p>
                                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="studio-image-gallery">
                                    {gallery.map((g) => (
                                        <figure key={g.id} className="border border-line">
                                            <img
                                                src={`${process.env.REACT_APP_BACKEND_URL}/api/ai/image/${g.id}`}
                                                alt={g.prompt}
                                                className="aspect-square w-full object-cover"
                                                loading="lazy"
                                            />
                                            <figcaption className="px-3 py-2 text-[11px] leading-snug text-ink-muted">
                                                {g.prompt.slice(0, 60)}
                                                {g.prompt.length > 60 ? "…" : ""}
                                            </figcaption>
                                        </figure>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "voice" && (
                    <div className="mt-8 grid gap-8 lg:grid-cols-2" data-testid="studio-voice">
                        <div className="border border-line bg-surface p-8">
                            <p className="eyebrow">Text to speech</p>
                            <textarea
                                data-testid="studio-tts-text"
                                value={ttsText}
                                onChange={(e) => setTtsText(e.target.value)}
                                rows={4}
                                maxLength={4096}
                                className="field mt-5 resize-none border border-line px-3"
                            />
                            <div className="mt-4 flex items-center gap-3">
                                <select
                                    data-testid="studio-tts-voice"
                                    value={voice}
                                    onChange={(e) => setVoice(e.target.value)}
                                    className="field"
                                >
                                    {VOICES.map((v) => (
                                        <option key={v} value={v}>
                                            {v}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={speak}
                                    disabled={busy || !ttsText.trim()}
                                    data-testid="studio-tts-btn"
                                    className="btn-primary shrink-0 disabled:opacity-60"
                                >
                                    Speak
                                </button>
                            </div>
                            {audioUrl && (
                                <div className="mt-6" data-testid="studio-tts-audio">
                                    <audio controls src={audioUrl} className="w-full" />
                                    <a
                                        href={audioUrl}
                                        download="panel-room-speech.mp3"
                                        data-testid="studio-tts-download"
                                        className="u-link mt-3 inline-flex items-center gap-2 text-sm text-brass"
                                    >
                                        <Download size={13} strokeWidth={1.5} />
                                        Download MP3
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="border border-line bg-surface p-8">
                            <p className="eyebrow">Speech to text</p>
                            <input
                                type="file"
                                accept="audio/*"
                                data-testid="studio-stt-file"
                                onChange={(e) => transcribe(e.target.files?.[0])}
                                className="field mt-5 text-sm"
                            />
                            <p className="mt-3 text-xs text-ink-muted">
                                Upload an audio file (max 25 MB) — it is transcribed with Whisper.
                            </p>
                            {transcript && (
                                <p
                                    className="mt-6 whitespace-pre-wrap border-t border-line pt-6 text-sm leading-relaxed"
                                    data-testid="studio-stt-result"
                                >
                                    {transcript}
                                </p>
                            )}
                        </div>

                        {voiceError && (
                            <p className="text-sm text-red-700 dark:text-red-400 lg:col-span-2" data-testid="studio-voice-error">
                                {voiceError}
                            </p>
                        )}
                    </div>
                )}

                {tab === "bookings" && (
                    <div className="mt-8 border border-line bg-surface p-8 sm:p-10" data-testid="studio-bookings">
                        <p className="eyebrow">Booking requests</p>
                        {bookings.length === 0 ? (
                            <p className="mt-5 text-sm text-ink-muted">No booking requests yet.</p>
                        ) : (
                            <table className="mt-5 w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-line text-[11px] uppercase tracking-[0.16em] text-ink-muted">
                                        <th className="py-3 pr-4">Name</th>
                                        <th className="py-3 pr-4">WhatsApp</th>
                                        <th className="py-3 pr-4">Exam</th>
                                        <th className="py-3 pr-4">Slot</th>
                                        <th className="py-3 pr-4">Readiness</th>
                                        <th className="py-3">Priority</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="bookings-table">
                                    {bookings.map((b, i) => (
                                        <tr key={i} className="border-b border-line/60 last:border-none">
                                            <td className="py-3 pr-4">{b.name}</td>
                                            <td className="py-3 pr-4 tabular-nums">{b.phone}</td>
                                            <td className="py-3 pr-4">{b.exam}</td>
                                            <td className="py-3 pr-4">{b.slot}</td>
                                            <td className="py-3 pr-4">
                                                {b.triage?.ai_classified ? (
                                                    <span className="text-xs text-ink">
                                                        {b.triage.readiness}
                                                        <span className="ml-1 text-ink-muted tabular-nums">
                                                            ({Math.round(b.triage.readiness_confidence * 100)}%)
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-ink-muted">—</span>
                                                )}
                                            </td>
                                            <td className="py-3">
                                                {b.triage?.ai_classified ? (
                                                    <span className="text-xs capitalize text-ink">{b.triage.priority}</span>
                                                ) : (
                                                    <span className="text-xs text-ink-muted">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === "messages" && (
                    <div className="mt-8 border border-line bg-surface p-8 sm:p-10" data-testid="studio-messages">
                        <p className="eyebrow">Contact messages</p>
                        {messages.length === 0 ? (
                            <p className="mt-5 text-sm text-ink-muted">No contact messages yet.</p>
                        ) : (
                            <ul className="mt-5 space-y-6">
                                {messages.map((m, i) => (
                                    <li
                                        key={i}
                                        className="border-b border-line/60 pb-6 last:border-none last:pb-0"
                                        data-testid={`studio-message-${i}`}
                                    >
                                        <div className="flex flex-wrap items-center gap-3">
                                            <p className="font-display text-base">{m.name}</p>
                                            <p className="text-xs text-ink-muted">{m.email}</p>
                                            {m.triage?.ai_classified && (
                                                <>
                                                    <span className="border border-brass px-2 py-0.5 text-[10px] uppercase tracking-widest text-brass">
                                                        {m.triage.category}
                                                    </span>
                                                    <span className="text-[11px] uppercase tracking-widest text-ink-muted">
                                                        urgency {m.triage.urgency}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <p className="mt-2 text-sm leading-relaxed text-ink">{m.message}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
};

export default Studio;
